/**
 * useWebPushNotification — owns the Web Push subscribe flow.
 *
 * Flow: request browser permission -> get the active SW registration ->
 * reuse or create a PushManager subscription -> map the browser's key shape
 * to the backend's -> POST it. Best-effort: every failure path logs and
 * returns false rather than throwing, so a push hiccup never breaks the app.
 *
 * Note: the SW registers only in production builds, so navigator.serviceWorker
 * .ready resolves only there. In `npm start` a subscribe attempt will hang at
 * that await — expected; test push against a production build.
 */

import { useCallback, useEffect, useState } from "react";

import { webPushNotificationService } from "../services/webPushNotification.service";
import { logger } from "../utils/logger";

function checkSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// base64url string -> Uint8Array. applicationServerKey needs a BufferSource,
// but the VAPID key arrives as a base64url string. Standard boilerplate.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export function useWebPushNotification() {
  const [isSupported] = useState<boolean>(checkSupported);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Sync our state from the live browser permission on mount.
  useEffect(() => {
    if (isSupported) setPermission(Notification.permission);
  }, [isSupported]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      logger.warn("Web Push not supported in this browser");
      return false;
    }

    setIsSubscribing(true);
    try {
      // 1. Ask the browser/OS for permission. If already granted this
      //    resolves instantly with no prompt; if denied, no prompt either.
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") {
        logger.info("Web Push permission not granted", { result });
        return false;
      }

      // 2. The subscription lives on the active SW registration.
      const registration = await navigator.serviceWorker.ready;

      // 3. Reuse an existing subscription if the browser already has one;
      //    otherwise create a fresh one with our VAPID key.
      let sub = await registration.pushManager.getSubscription();
      if (!sub) {
        const vapidKey = await webPushNotificationService.getVapidPublicKey();
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true, // required by Chrome; every push must be visible
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      // 4. toJSON() gives endpoint + already-base64url-encoded keys — exactly
      //    the backend's shape (after the key renames).
      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        logger.error("Push subscription missing endpoint or keys");
        return false;
      }

      await webPushNotificationService.saveSubscription({
        endpoint: json.endpoint,
        p256dh_key: json.keys.p256dh,
        auth_key: json.keys.auth,
        user_agent: navigator.userAgent,
      });

      logger.info("Web Push subscription saved");
      return true;
    } catch (err) {
      // Includes ApiError(503) when VAPID is unset — treat as "unavailable".
      logger.error("Web Push subscribe failed", err as Error);
      return false;
    } finally {
      setIsSubscribing(false);
    }
  }, [isSupported]);

  return { permission, isSupported, isSubscribing, subscribe };
}
